import React, { useEffect, useMemo, useState } from 'react';

export default function CompanyValueModal({
                                              show,
                                              mode = 'create',                 // 'create' | 'edit'
                                              onClose,
                                              onSubmit,                         // (dto) => Promise
                                              initial = null,                   // when editing
                                              cycleId,
                                              remaining = 10,                   // remaining toward 10.00 target (informational)
                                          }) {
    const isEdit = mode === 'edit';

    const [name, setName] = useState('');
    const [weight, setWeight] = useState('');
    const [description, setDescription] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!show) return;
        setError(null);
        setBusy(false);
        if (isEdit && initial) {
            setName(initial.name ?? '');
            setWeight(String(initial.weight ?? ''));
            setDescription(initial.description ?? '');
        } else {
            setName('');
            // convenience: suggest up to 2.5 or remaining, whichever smaller
            const suggested = Math.max(0, Math.min(2.5, Number(remaining) || 0)).toFixed(2);
            setWeight(suggested);
            setDescription('');
        }
    }, [show, isEdit, initial, remaining]);

    const parsedWeight = useMemo(() => Number(weight) || 0, [weight]);
    const canSubmit = name.trim() && parsedWeight > 0;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        try {
            const dto = {
                cycle_id: cycleId,
                name: name.trim(),
                description: description.trim() || null,
                weight: parseFloat(parsedWeight.toFixed(2)),
            };
            await onSubmit(dto);
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to save company value.';
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <div className="modal-dialog">
                <form className="modal-content" onSubmit={submit}>
                    <div className="modal-header">
                        <h5 className="modal-title">{isEdit ? 'Edit Company Value' : 'Add Company Value'}</h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}

                        <div className="mb-3">
                            <label className="form-label">Name</label>
                            <input className="form-control" value={name} onChange={e => setName(e.target.value)} required />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Weight</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                value={weight}
                                onChange={e => setWeight(e.target.value)}
                                required
                            />
                            <div className="form-text">Target total for Company Values: 10.00 (remaining hint: {Number(remaining).toFixed(2)})</div>
                        </div>

                        <div className="mb-0">
                            <label className="form-label">Description (optional)</label>
                            <textarea rows={3} className="form-control" value={description} onChange={e => setDescription(e.target.value)} />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={!canSubmit || busy}>
                            {busy ? 'Saving…' : (isEdit ? 'Save Changes' : 'Save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
